// src/components/MyCalendarDebugger.jsx
import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { isIosCapacitor } from "../utils/nativeHttp";
import { getManualAuth, isManualAuthLikelyValid } from "../utils/manualAuth";
import fsClient from "../data/firestoreClient";

function norm(s) {
  return (s || "").toString().trim().toLowerCase();
}

export default function MyCalendarDebugger() {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [manualAuthStatus, setManualAuthStatus] = useState(null);
  const [ownerUidResults, setOwnerUidResults] = useState(null);
  const [ownerEmailResults, setOwnerEmailResults] = useState(null);
  const [uidResults, setUidResults] = useState(null);
  const [emailResults, setEmailResults] = useState(null);

  useEffect(() => {
    const u = auth && auth.currentUser;
    if (u) setUserStatus({ uid: u.uid || null, email: u.email || null });
    else setUserStatus(null);
    try { setManualAuthStatus(getManualAuth() || null); } catch (_) { setManualAuthStatus(null); }
  }, []);

  const runChecks = async () => {
    setRunning(true);
    setError(null);
    setOwnerUidResults(null);
    setOwnerEmailResults(null);
    setUidResults(null);
    setEmailResults(null);

    try {
      const webUser = auth && auth.currentUser ? auth.currentUser : null;
      const ma = (() => { try { return getManualAuth(); } catch (_) { return null; } })();

      let uid = webUser && webUser.uid ? String(webUser.uid) : "";
      let email = webUser && webUser.email ? norm(webUser.email) : "";

      if ((!uid || !email) && isIosCapacitor() && ma && isManualAuthLikelyValid(ma)) {
        if (!uid && ma.uid) uid = String(ma.uid);
        if (!email && ma.email) email = norm(ma.email);
      }

      if (!uid && !email) {
        setError("No hay identidad efectiva. auth.currentUser y manualAuth no tienen uid/email.");
        setRunning(false);
        return;
      }

      let ownerUidMatches = [];
      if (uid) {
        ownerUidMatches = await fsClient.listCollectionByPath(
          "businesses",
          { where: [{ field: "ownerUid", op: "EQUAL", value: uid }], limit: 200 },
          { db }
        );
      }
      setOwnerUidResults(Array.isArray(ownerUidMatches) ? ownerUidMatches : []);

      let ownerEmailMatches = [];
      if (email) {
        ownerEmailMatches = await fsClient.listCollectionByPath(
          "businesses",
          { where: [{ field: "ownerEmail", op: "EQUAL", value: email }], limit: 200 },
          { db }
        );
      }
      setOwnerEmailResults(Array.isArray(ownerEmailMatches) ? ownerEmailMatches : []);

      const allBusinesses = await fsClient.listCollectionByPath("businesses", {}, { db });
      const bizArr = Array.isArray(allBusinesses) ? allBusinesses : [];
      const uidArr = [];
      const emailArr = [];

      for (const b of bizArr) {
        const businessId = b && (b.id || b.businessId) ? String(b.id || b.businessId) : null;
        if (!businessId) continue;

        let employees = [];
        try {
          employees = await fsClient.listCollectionByPath(`businesses/${businessId}/employees`, {}, { db });
        } catch (_) {
          continue;
        }

        for (const emp of employees || []) {
          const empUidCandidates = [
            emp?.userUid,
            emp?.uid,
            emp?.employeeUserUid,
            emp?.employeeUid,
            emp?.userId,
          ].filter(Boolean).map(String);

          const empEmailCandidates = [
            emp?.email,
            emp?.employeeEmail,
            emp?.workerEmail,
          ].filter(Boolean).map(norm);

          const row = {
            employeeId: emp?.id || emp?.employeeId || null,
            businessId,
            businessName: b?.name || b?.businessName || b?.title || null,
            employeeData: emp,
          };

          if (uid && empUidCandidates.includes(String(uid))) uidArr.push(row);
          if (email && empEmailCandidates.includes(String(email))) emailArr.push(row);
        }
      }

      setUidResults(uidArr);
      setEmailResults(emailArr);
    } catch (err) {
      setError(String(err && err.message ? err.message : err));
    } finally {
      setRunning(false);
    }
  };

  const renderBox = (title, value) => (
    <div style={{ marginTop: 14 }}>
      <h4>{title}</h4>
      {value === null ? (
        <div style={{ color: "#94a3b8" }}>No ejecutado</div>
      ) : Array.isArray(value) ? (
        value.length === 0 ? (
          <div style={{ color: "#94a3b8" }}>Sin resultados</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {value.map((r, idx) => (
              <div
                key={`${title}-${r.businessId || "x"}-${r.employeeId || idx}`}
                style={{ border: "1px solid #e6eef6", padding: 10, borderRadius: 6 }}
              >
                <div><strong>businessId:</strong> {r.businessId || r.id || "(sin businessId)"}</div>
                {r.businessName ? <div><strong>businessName:</strong> {r.businessName}</div> : null}
                {r.employeeId ? <div><strong>employeeId:</strong> {r.employeeId}</div> : null}
                <pre style={{ marginTop: 8, background: "#f8fafc", padding: 8, overflow: "auto", fontSize: 12 }}>
                  {JSON.stringify(r, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        )
      ) : (
        <pre style={{ background: "#f8fafc", padding: 8, overflow: "auto", fontSize: 12 }}>
          {JSON.stringify(value, null, 2)}
        </pre>
      )}
    </div>
  );

  return (
    <div style={{ padding: 14, maxWidth: 980 }}>
      <h3>MyCalendar Debugger (build de diagnóstico)</h3>
      <div style={{ marginBottom: 8 }}>
        <strong>Runtime:</strong> {isIosCapacitor() ? "iOS Capacitor" : "Web/Android"}
      </div>
      <div style={{ marginBottom: 8 }}>
        <strong>Usuario actual Firebase:</strong> {userStatus ? `${userStatus.email || "(sin email)"} — ${userStatus.uid || "(sin uid)"}` : "No autenticado"}
      </div>
      <div style={{ marginBottom: 8 }}>
        <strong>ManualAuth actual:</strong>
        <pre style={{ background: "#f8fafc", padding: 8, overflow: "auto", fontSize: 12 }}>
          {JSON.stringify(manualAuthStatus, null, 2)}
        </pre>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button
          onClick={runChecks}
          disabled={running}
          style={{ padding: "8px 12px", background: "#059669", color: "#fff", border: "none", borderRadius: 6 }}
        >
          {running ? "Comprobando..." : "Ejecutar comprobaciones"}
        </button>
      </div>
      {error && <div style={{ color: "red", marginBottom: 8 }}>{error}</div>}
      {renderBox("Owner matches por ownerUid", ownerUidResults)}
      {renderBox("Owner matches por ownerEmail", ownerEmailResults)}
      {renderBox("Employee matches por UID", uidResults)}
      {renderBox("Employee matches por email", emailResults)}
    </div>
  );
}
